
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import confetti from 'canvas-confetti';

export const ShopSuccessPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'VERIFYING' | 'SUCCESS' | 'ERROR'>('VERIFYING');
    const processedRef = useRef(false);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const isMock = searchParams.get('mock_payment');

        if (!sessionId || processedRef.current) return;
        processedRef.current = true;

        verifyPayment(sessionId, isMock === 'true');
    }, [searchParams]);

    const verifyPayment = async (sessionId: string, isMock: boolean) => {
        try {
            // In a real flow, the webhook usually handles it before we get here,
            // but we can have a "sync" endpoint or just check the order status.
            // For the Mock flow, we explicitly trigger the confirmation if needed.

            if (isMock) {
                await api.post('/payment/mock-webhook-confirm', { sessionId });
            }

            // Simple delay for dramatic effect
            setTimeout(() => {
                setStatus('SUCCESS');
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 },
                    colors: ['#d4af37', '#fdfbf7', '#22c55e']
                });
                toast.success('Paiement validé ! Votre compte a été crédité.');
            }, 1500);

        } catch (error) {
            console.error(error);
            setStatus('ERROR');
        }
    };

    return (
        <div className="min-h-screen bg-navy-950 flex flex-col items-center justify-center p-4">
            <div className="bg-navy-900 border border-gold-500/30 p-8 rounded-2xl max-w-md w-full text-center shadow-2xl relative overflow-hidden">
                {status === 'VERIFYING' && (
                    <>
                        <div className="w-16 h-16 border-4 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                        <h2 className="text-2xl font-bold text-active-text mb-2 animate-pulse">Vérification...</h2>
                        <p className="text-navy-300">Nous sécurisons vos souvenirs.</p>
                    </>
                )}

                {status === 'SUCCESS' && (
                    <div className="animate-in fade-in zoom-in duration-500">
                        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl border-2 border-emerald-500 shadow-glow-emerald">
                            ✅
                        </div>
                        <h2 className="text-3xl font-bold text-emerald-400 mb-2 font-serif">Paiement Réussi !</h2>
                        <p className="text-cream-200 mb-8">
                            Merci de votre soutien. Votre solde a été mis à jour.
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="btn-vintage w-full py-3"
                        >
                            Retour au Dashboard
                        </button>
                    </div>
                )}

                {status === 'ERROR' && (
                    <div>
                        <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl border-2 border-red-500">
                            ⚠️
                        </div>
                        <h2 className="text-2xl font-bold text-red-400 mb-2">Une erreur est survenue</h2>
                        <p className="text-navy-300 mb-8">
                            Le paiement n'a pas pu être confirmé automatiquement. Si vous avez été débité, contactez le support.
                        </p>
                        <button
                            onClick={() => navigate('/shop')}
                            className="px-6 py-2 border border-navy-600 rounded text-navy-400 hover:text-white"
                        >
                            Retour à la boutique
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
